import { supabase } from '../integrations/client.ts';

export const auth = {
  signIn: async (email: string, password: string) => {
    
    try {
      const result = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      return result;
    } catch (error: any) {
      return { data: { session: null, user: null }, error };
    }
  },
  
  signInWithGoogle: async () => {

    try {
      // Use environment variable for production URL, fallback to window.location.origin for dev
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;

      const result = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: appUrl
        }
      });

      return result;
    } catch (error) {
      throw error;
    }
  },
  
  signUp: async (email: string, password: string, metadata?: any) => {

    try {
      // Use environment variable for production URL, fallback to window.location.origin for dev
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;

      const result = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          // Redirect to Verified page - email is verified by Supabase backend
          // We append a special param to skip auto-session detection
          emailRedirectTo: `${appUrl}/?view=verified`,
        }
      });

      return result;
    } catch (error) {
      throw error;
    }
  },
  
  signOut: async () => {
    
    try {
      const result = await supabase.auth.signOut();
      return result;
    } catch (error) {
      throw error;
    }
  },
  
  getCurrentUser: async () => {

    try {
      const result = await supabase.auth.getUser();
      return result;
    } catch (error) {
      throw error;
    }
  },

  getCurrentSession: async () => {

    try {
      const result = await supabase.auth.getSession();
      return result.data;
    } catch (error) {
      return { session: null, error };
    }
  },

  onAuthStateChange: (callback: (event: string, session: any) => void) => {

    try {
      return supabase.auth.onAuthStateChange(callback);
    } catch (error) {
      throw error;
    }
  },

  // Get a valid session, refreshing if necessary
  getValidSession: async () => {

    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error || !session) {
        return { session: null, error: error || new Error('No active session') };
      }

      // Check if token is expired or about to expire (within 60 seconds)
      const expiresAt = session.expires_at || 0;
      const now = Math.floor(Date.now() / 1000);
      const expiresIn = expiresAt - now;

      if (expiresIn < 60) {
        // Token expired or expiring soon - refresh it
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError || !refreshedSession) {
          return { session: null, error: refreshError || new Error('Failed to refresh session') };
        }

        return { session: refreshedSession, error: null };
      }

      return { session, error: null };
    } catch (error) {
      return { session: null, error };
    }
  },

  // NOTE: The following methods have been removed as they are replaced by Supabase's built-in auth methods:
  // - requestPasswordReset() -> Use supabase.auth.resetPasswordForEmail() directly
  // - verifyEmailWithCode() -> Use supabase.auth.verifyOtp() directly
  // - resendVerificationEmail() -> Use supabase.auth.resend() directly
  // - resetPasswordWithToken() -> Use supabase.auth.updateUser() after token verification
};